/**
 *
 * CartExpireModal
 *
 */

import React from 'react';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBusinessTime, faSpinner, faShoppingCart } from '@fortawesome/free-solid-svg-icons';
import ExpireTime from '@ts/components/ExpireTime';
import actions from '@ts/core/actions';
import selectors from '@ts/core/selectors';

const Backdrop = styled.div`
    background: #000;
    opacity: 0.3;
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 1050;
`;

const Scrollable = styled.div`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    overflow: auto;
    /* overflow-y: scroll; */
    z-index: 1060;
`;

const ModalWrapper = styled.div`
    display: block;
    width: 760px;
    margin-left: -380px;
    margin-top: -160px;
    outline: none;
    position: absolute;
    top: 50%;
    overflow: visible;
    left: 50%;
    z-index: 1050;
    background-color: #ffffff;
    border-radius: 3px;
    background-clip: padding-box;
    box-shadow: 0 3px 7px rgba(0, 0, 0, 0.3);
    box-sizing: border-box;
    padding: 25px;

    @media (max-width: 767px) {
        width: 560px;
        margin-left: -280px;
    }

    @media (max-width: 570px) {
        width: 460px;
        margin-left: -230px;
    }

    @media (max-width: 470px) {
        width: 360px;
        margin-left: -180px;
    }

    @media (max-width: 370px) {
        width: 310px;
        margin-left: -155px;
    }
`;

const Actions = styled.div`
    padding: 35px 0;
    padding-bottom: 0;
    display: flex;
    justify-content: center;
`;

const ActionButton = styled.button`
    background: #62ba37;
    color: #fff;
    border: 2px solid #62ba37;
    border-radius: 3px;
    padding: 9px 16px;
    text-transform: uppercase;
    font-weight: 700;
    letter-spacing: 1px;
    box-shadow: 0 1px 3px #c3c3c3;
    will-change: background, color;
    transition: background 0.3s ease, color 0.3s ease;
    outline: none;

    svg {
        margin-right: 10px;
    }

    ${(props) =>
        props.disabled &&
        css`
            opacity: 0.7;
        `}

    &:hover {
        ${(props) =>
            !props.disabled &&
            css`
                background: transparent;
                color: #333;
                cursor: pointer;
            `}
    }
`;

const ActionExtendButton = styled(ActionButton)`
    margin-right: 15px;
    border-color: #888;
    background-color: #888;
`;

const ActionCheckOutButton = styled(ActionButton)``;

function Modal() {
    const dispatch = useDispatch();
    const ref = React.useRef();
    const cartExpireDiff = useSelector(selectors.cart.makeSelectExpireDiff());
    const cartIsExtending = useSelector(selectors.cart.makeSelectIsExtending());
    const onExtend = React.useCallback(() => {
        dispatch(actions.cart.extend());
    }, [dispatch]);

    const onCheckOut = React.useCallback(() => {
        try {
            onExtend();
            window.browserHistory.push('/checkout');
        } catch {}
    }, [onExtend]);

    React.useEffect(() => {
        try {
            document.body.classList.add('modal-cart-expire');
        } catch {}

        return () => {
            try {
                document.body.classList.remove('modal-cart-expire');
            } catch {}
        };
    }, []);

    return (
        <React.Fragment>
            <Scrollable>
                <ModalWrapper ref={ref}>
                    <ExpireTime
                        hours={cartExpireDiff.hours}
                        minutes={cartExpireDiff.minutes}
                        seconds={cartExpireDiff.seconds}
                    />

                    <Actions>
                        {cartExpireDiff.totalSeconds <= 300 && (
                            <ActionExtendButton disabled={cartIsExtending} onClick={onExtend}>
                                <FontAwesomeIcon
                                    fixedWidth
                                    icon={cartIsExtending ? faSpinner : faBusinessTime}
                                    spin={cartIsExtending}
                                />
                                Extend Time By 5 Minutes
                            </ActionExtendButton>
                        )}
                        <ActionCheckOutButton onClick={onCheckOut}>
                            <FontAwesomeIcon fixedWidth icon={faShoppingCart} />
                            Check Out Now
                        </ActionCheckOutButton>
                    </Actions>
                </ModalWrapper>
            </Scrollable>
            <Backdrop />
        </React.Fragment>
    );
}

function CartExpireModal() {
    const cartExpireModalIsVisible = useSelector(selectors.cart.makeSelectExpireModalIsVisible());
    const cartIsLoaded = useSelector(selectors.cart.makeSelectIsLoaded());
    const checkoutPosition = useSelector(selectors.checkout.makeSelectPosition());
    const isOpen = cartExpireModalIsVisible && checkoutPosition < 2 && cartIsLoaded;

    if (!isOpen || /^\/checkout.*?/gi.test(window.location.pathname)) {
        return null;
    }

    return <Modal />;
}

export default CartExpireModal;
